import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "../hooks/useTranslation";
import { supabase } from "../lib/supabase";
import { isQualityAssuranceUiEnabled } from "../config/featureFlags";
import QABadges from "./QABadges";

export default function ListingCard({ item }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showFullscreen, setShowFullscreen] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  
  const photos = item.photos || ["/placeholder.jpg"];
  const hasMultiplePhotos = photos.length > 1;
  
  const getCategoryClass = (category) => {
    const categoryMap = {
      'Pre-natal': 'category-prenatal',
      'Clothing': 'category-clothing',
      'Gear': 'category-gear', 
      'Toys': 'category-toys',
      'Feeding': 'category-feeding',
      'Nursery': 'category-nursery',
      'Other': 'category-other'
    };
    return categoryMap[category] || 'category-other';
  };

  // Check if listing is favorited
  useEffect(() => {
    const checkFavoriteStatus = async () => {
      if (!user || !item.id) return;
      
      try {
        const { data, error } = await supabase
          .from('favorites')
          .select('id')
          .eq('user_id', user.id)
          .eq('listing_id', item.id)
          .single();
        
        if (error && error.code !== 'PGRST116') {
          console.error('Error checking favorite status:', error);
          return;
        }
        
        setIsFavorited(!!data);
      } catch (err) {
        console.error('Error checking favorite status:', err);
      }
    };

    checkFavoriteStatus();
  }, [user, item.id]);

  // Toggle favorite status
  const toggleFavorite = async (e) => {
    e.stopPropagation();
    
    if (!user) {
      // Redirect to login if not authenticated
      nav('/login');
      return;
    }
    
    setFavoriteLoading(true);
    
    try {
      if (isFavorited) {
        // Remove from favorites
        const { error } = await supabase
          .from('favorites')
          .delete()
          .eq('user_id', user.id)
          .eq('listing_id', item.id);
        
        if (error) throw error;
        setIsFavorited(false);
      } else {
        // Add to favorites
        const { error } = await supabase
          .from('favorites')
          .insert({
            user_id: user.id,
            listing_id: item.id
          });
        
        if (error) throw error;
        setIsFavorited(true);
      }
    } catch (err) {
      console.error('Error toggling favorite:', err);
      // You could add a toast notification here
    } finally {
      setFavoriteLoading(false);
    }
  };

  return (
    <div
      className="listing-card animate-fade-in-up"
      onClick={() => nav(`/listing/${item.id}`)}
    >
      {/* Enhanced Image Container with Carousel */}
      <div className="relative overflow-hidden group">
        <div className="relative">
          <img
            className="listing-img"
            src={photos[currentImageIndex]}
            alt={item.title}
            onClick={(e) => {
              e.stopPropagation();
              setShowFullscreen(true);
            }}
          />
          
          {/* Photo Counter */}
          {hasMultiplePhotos && (
            <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-1.5 py-0.5 rounded-full">
              {currentImageIndex + 1}/{photos.length}
            </div>
          )}
          
          {/* Category Badge */}
          <div className="absolute top-2 left-2">
            <span className={`category-badge ${getCategoryClass(item.category)}`}>
              {item.category}
            </span>
          </div>


          {/* Tags */}
          {item.tags && item.tags.length > 0 && (
            <div className={`absolute ${hasMultiplePhotos ? 'top-8' : 'top-2'} right-2 flex flex-col gap-0.5`}>
              {item.tags.slice(0, 2).map((tagId) => {
                const tagMap = {
                  'moving-out': { label: 'Moving Out', emoji: '📦', color: 'bg-orange-100 text-orange-800' },
                  'give-away': { label: 'Give Away', emoji: '🎁', color: 'bg-green-100 text-green-800' },
                  'urgent': { label: 'Urgent', emoji: '⚡', color: 'bg-red-100 text-red-800' },
                  'bundle': { label: 'Bundle', emoji: '📦', color: 'bg-purple-100 text-purple-800' },
                  'new': { label: 'New', emoji: '✨', color: 'bg-blue-100 text-blue-800' },
                  'negotiable': { label: 'Negotiable', emoji: '💰', color: 'bg-yellow-100 text-yellow-800' },
                  'pickup-only': { label: 'Pickup', emoji: '🚚', color: 'bg-indigo-100 text-indigo-800' }
                };
                const tag = tagMap[tagId];
                if (!tag) return null;
                return (
                  <span key={tagId} className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs font-medium rounded-full ${tag.color}`}>
                    <span className="text-xs">{tag.emoji}</span>
                    <span className="hidden sm:inline text-xs">{tag.label}</span>
                  </span>
                );
              })}
              {item.tags.length > 2 && (
                <span className="inline-flex items-center justify-center w-5 h-5 bg-slate-600 text-white text-xs font-medium rounded-full">
                  +{item.tags.length - 2}
                </span>
              )}
            </div>
          )}
          
          {/* Navigation Arrows */}
          {hasMultiplePhotos && (
            <>
              <button
                className="absolute left-2 top-1/2 transform -translate-y-1/2 w-8 h-8 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : photos.length - 1));
                }}
              >
                <svg className="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                className="absolute right-2 top-1/2 transform -translate-y-1/2 w-8 h-8 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentImageIndex((prev) => (prev < photos.length - 1 ? prev + 1 : 0));
                }}
              >
                <svg className="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}
          
          
          {/* Photo Dots Indicator */}
          {hasMultiplePhotos && (
            <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex gap-1">
              {photos.map((_, index) => (
                <button
                  key={index}
                  className={`w-2 h-2 rounded-full transition-colors duration-200 ${
                    index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentImageIndex(index);
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Card Content */}
      <div className="p-3">
        {/* Title and Age */}
        <div className="mb-2">
          <h3 className="font-semibold text-slate-900 text-sm mb-1 line-clamp-2">
            {item.title}
          </h3>
          {item.location && (
            <div className="text-xs text-slate-500 flex items-center gap-1 mb-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="truncate">{item.location}</span>
            </div>
          )}
          {item.age_range && (
            <div className="text-xs text-slate-500 flex items-center gap-1">
              <span>👶</span>
              <span className="truncate">
                {item.age_range === '0-3M' && '0-3M (Newborn)'}
                {item.age_range === '3-6M' && '3-6M'}
                {item.age_range === '6-12M' && '6-12M'}
                {item.age_range === '12-24M' && '12-24M (Toddler)'}
                {item.age_range === '2-4Y' && '2-4Y (Preschool)'}
                {item.age_range === '4Y+' && '4Y+ (School)'}
                {item.age_range === 'All Ages' && 'All Ages'}
                {!['0-3M', '3-6M', '6-12M', '12-24M', '2-4Y', '4Y+', 'All Ages'].includes(item.age_range) && item.age_range}
              </span>
            </div>
          )}
        </div>

        {/* Price and Distance */}
        <div className="flex items-center justify-between">
          <div className="text-lg font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
            ${(item.price_cents / 100).toFixed(2)}
          </div>
          
          <div className="text-xs text-slate-500 flex items-center gap-1">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span>{item.distance_km?.toFixed(1)}km</span>
          </div>
        </div>

        {/* Condition Indicator with Heart Button */}
        {item.condition && (
          <div className="mt-2 pt-2 border-t border-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <div className={`w-1.5 h-1.5 rounded-full ${
                  item.condition === 'excellent' ? 'bg-green-500' :
                  item.condition === 'good' ? 'bg-yellow-500' :
                  item.condition === 'fair' ? 'bg-orange-500' : 'bg-red-500'
                }`}></div>
                <span className="text-xs text-slate-500">{t(`condition.${item.condition}`)}</span>
              </div>
              
              {/* Heart Button */}
              <button 
                className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors duration-200 ${
                  isFavorited 
                    ? 'bg-red-500 text-white hover:bg-red-600' 
                    : 'bg-slate-100 hover:bg-red-100 hover:text-red-500'
                } ${favoriteLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={toggleFavorite}
                disabled={favoriteLoading}
                title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
              >
                <svg className="w-3 h-3" fill={isFavorited ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* QA Badges */}
        {isQualityAssuranceUiEnabled && (item.qa_badges?.length > 0 || item.qa_score > 0) && (
          <div className="mt-2 pt-2 border-t border-slate-100">
            <QABadges 
              badges={item.qa_badges || []} 
              qaScore={item.qa_score || 0}
              compact={true}
            />
          </div>
        )}
      </div>
      
      {/* Fullscreen Photo Modal */}
      {showFullscreen && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <div className="relative max-w-4xl max-h-full">
            <button
              className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
              onClick={() => setShowFullscreen(false)}
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            {hasMultiplePhotos && (
              <>
                <button
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300"
                  onClick={() => setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : photos.length - 1))}
                >
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300"
                  onClick={() => setCurrentImageIndex((prev) => (prev < photos.length - 1 ? prev + 1 : 0))}
                >
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </>
            )}
            
            <img
              src={photos[currentImageIndex]}
              alt={item.title}
              className="max-w-full max-h-full object-contain"
            />
            
            {hasMultiplePhotos && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                {photos.map((_, index) => (
                  <button
                    key={index}
                    className={`w-3 h-3 rounded-full transition-colors duration-200 ${
                      index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                    }`}
                    onClick={() => setCurrentImageIndex(index)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}